import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, Popover, RadioGroup } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { useIsMobile } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { Button, RadioItem, useRadioControlStyles, useToggle } from '@uplift/ui-v2';

import { FinanceMonthPeriodFilter } from '../../types';

export type PeriodFilterProps = {
  periodFilter: FinanceMonthPeriodFilter;
  date: string;
  className?: string;
};

export const PeriodFilter: React.VFC<PeriodFilterProps> = ({ periodFilter, date, className }) => {
  const history = useHistory();
  const { classes: radioClasses } = useRadioControlStyles();
  const isMobile = useIsMobile();
  const [ anchorEl, setAnchorEl ] = useState<HTMLElement>();
  const [ open, handleOpen, handleClose ] = useToggle(false);

  const onFilterChange = (filter: FinanceMonthPeriodFilter) => {
    history.push(`/finance/${date}/${filter}`);
    handleClose();
  };

  const currentDate = CalendarDateService.date(date);
  const lastDayInMonth = currentDate.daysInMonth();
  const allLabel = `${currentDate.format('MMMM')} 1–${lastDayInMonth}`;
  const firstHalf = `${currentDate.format('MMMM')} 1–15`;
  const secondHalf = `${currentDate.format('MMMM')} 16-${lastDayInMonth}`;

  const titleDict = useMemo(() => ({
    [FinanceMonthPeriodFilter.all]: allLabel,
    [FinanceMonthPeriodFilter.first]: firstHalf,
    [FinanceMonthPeriodFilter.second]: secondHalf,
  }), [ allLabel, firstHalf, secondHalf ]);

  const periodFilterItems = useMemo(() => {
    return [
      {
        value: FinanceMonthPeriodFilter.all,
        label: allLabel,
      },
      {
        value: FinanceMonthPeriodFilter.first,
        label: firstHalf,
      },
      {
        value: FinanceMonthPeriodFilter.second,
        label: secondHalf,
      },
    ];
  }, [ allLabel, firstHalf, secondHalf ]);

  const title = titleDict[periodFilter];

  const filterInput = (
    <RadioGroup
      value={periodFilter}
      onChange={(_, value: FinanceMonthPeriodFilter) => onFilterChange(value)}
    >
      {periodFilterItems.map((periodFilterItem, index) => {
        return (
          <RadioItem
            key={index}
            value={periodFilterItem}
            className={radioClasses.radioRoot}
            color="secondary"
          />
        );
      })}
    </RadioGroup>
  );

  return (
    <Box display="flex" alignItems="center">
      <Button
        ref={setAnchorEl}
        onClick={handleOpen}
        startIcon={<FilterList />}
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        className={className}
      >
        {title}
      </Button>

      <Popover
        open={open}
        keepMounted
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box py={1} pl={2} maxWidth="320px">
          {filterInput}
        </Box>
      </Popover>
    </Box>
  );
};
